<!-- src/App.vue - Enhanced with FORCED Location Service Integration -->
<template>
  <div id="app" class="min-h-screen bg-gray-50 overflow-x-hidden w-full max-w-full">
    <!-- Loading Screen — shown until auth init + redirect check are complete -->
    <div
      v-if="!appReady"
      class="fixed inset-0 bg-white flex items-center justify-center z-50"
    >
      <div class="text-center">
        <div
          class="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"
        ></div>
        <h2 class="text-xl font-semibold text-gray-700">Loading ExoTrack...</h2>
        <p class="text-gray-500 mt-2">
          Please wait while we initialize the application
        </p>
      </div>
    </div>

    <!-- Main Application -->
    <div v-else class="min-h-screen w-full overflow-x-hidden">
      <!-- Navigation Bar (only show when authenticated) -->
      <NavBar v-if="authStore.isAuthenticated && !isEmployeePortal" />

      <!-- Main Content Area -->
      <main
        :class="authStore.isAuthenticated ? 'pt-0' : ''"
        class="min-h-screen w-full overflow-x-hidden"
      >
        <!-- Router View with Transitions -->
        <router-view v-slot="{ Component, route }">
          <transition name="fade">
            <component :is="Component" :key="route.path" />
          </transition>
        </router-view>
      </main>
    </div>

    <!-- Session Warning Modal -->
    <SessionTimeoutModal
      v-if="showSessionWarning"
      :timeRemaining="sessionTimeRemaining"
      @extend="extendSession"
      @logout="handleSessionLogout"
    />

    <!-- NEW: Employee Forced Location Notification -->
    <EmployeeLocationNotification />

    <!-- Toast Notifications -->
    <ToastNotification />

    <!-- Admin Location Permission Modal (Optional - for admins only) -->
    <div
      v-if="showLocationPermissionModal && authStore.isAdmin"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="closeLocationPermissionModal"
    >
      <div
        class="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white"
        @click.stop
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">
            Enable Location Services (Optional)
          </h3>
          <button
            @click="closeLocationPermissionModal"
            class="text-gray-400 hover:text-gray-600"
          >
            <X class="w-6 h-6" />
          </button>
        </div>

        <div class="mb-6">
          <div class="flex items-center mb-4">
            <MapPin class="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <p class="text-sm text-gray-700">
                As an admin, you can optionally enable location services for
                better security monitoring.
              </p>
            </div>
          </div>

          <div class="space-y-3 text-sm text-gray-600">
            <div class="flex items-center">
              <Shield class="w-4 h-4 text-green-500 mr-2" />
              <span>Enhanced security monitoring</span>
            </div>
            <div class="flex items-center">
              <Building class="w-4 h-4 text-blue-500 mr-2" />
              <span>Automatic office/field detection</span>
            </div>
            <div class="flex items-center">
              <Clock class="w-4 h-4 text-purple-500 mr-2" />
              <span>Admin activity tracking</span>
            </div>
          </div>
        </div>

        <div class="flex justify-end space-x-3">
          <button
            @click="closeLocationPermissionModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Skip
          </button>
          <button
            @click="enableLocationServices"
            :disabled="locationLoading"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md transition-colors flex items-center"
          >
            <div
              v-if="locationLoading"
              class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
            ></div>
            Enable Location
          </button>
        </div>
      </div>
    </div>

    <!-- Location Status Indicator (Admin Only) -->
    <div
      v-if="
        showLocationStatus && authStore.isAuthenticated && authStore.isAdmin
      "
      class="fixed bottom-4 right-4 bg-white shadow-lg rounded-lg p-3 border z-40 transition-all duration-300"
      :class="locationStatusClasses"
    >
      <div class="flex items-center space-x-2">
        <component
          :is="locationStatusIcon"
          :class="['w-4 h-4', locationStatusColor]"
        />
        <span class="text-xs font-medium">{{ locationStatusText }}</span>
        <button
          v-if="!locationEnabled && !locationLoading"
          @click="requestLocationPermission"
          class="text-xs text-blue-600 hover:text-blue-800 underline ml-2"
        >
          Enable
        </button>
      </div>
    </div>

    <!-- Offline Indicator -->
    <div
      v-if="isOffline"
      class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-yellow-500 text-white px-4 py-3 rounded-lg shadow-lg z-50"
    >
      <div class="flex items-center">
        <div class="w-4 h-4 bg-white rounded-full mr-3 animate-pulse"></div>
        <div>
          <p class="font-medium">You're offline</p>
          <p class="text-sm opacity-90">
            Some features may not work until you reconnect.
          </p>
        </div>
      </div>
    </div>

    <!-- Location Error Toast (Admin Only) -->
    <div
      v-if="locationError && showLocationError && authStore.isAdmin"
      class="fixed top-4 right-4 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 max-w-sm"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <AlertTriangle class="w-4 h-4 mr-2" />
          <div>
            <p class="font-medium text-sm">Admin Location Error</p>
            <p class="text-xs opacity-90">{{ locationError }}</p>
          </div>
        </div>
        <button
          @click="showLocationError = false"
          class="ml-3 text-white hover:text-gray-200"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { useCurrency } from '@/composables/useCurrency';
import NavBar from './components/navigation/NavBar.vue';
import SessionTimeoutModal from './components/ui/modals/SessionTimeoutModal.vue';
import EmployeeLocationNotification from './components/location/EmployeeLocationNotification.vue';
import ToastNotification from './components/ui/notifications/ToastNotification.vue';
import {
  X,
  MapPin,
  Shield,
  Building,
  Clock,
  Navigation,
  NavigationOff,
  Loader,
  AlertTriangle,
} from 'lucide-vue-next';

// Stores and router
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();

// Both portals have their own full navigation (AdminLayout sidebar + EmployeeLayout tab bar).
// Never show the App-level NavBar when either portal is active.
const isEmployeePortal = computed(() =>
  route.path.startsWith('/employee') || authStore.isEmployee ||
  route.path.startsWith('/admin')    || authStore.isAdmin
);

// Currency — loaded once globally so every view uses the correct symbol
const { loadCurrency } = useCurrency();

// State
const appReady = ref(false); // true only after auth init + redirect check complete
const isOffline = ref(false);
const showSessionWarning = ref(false);
const sessionWarningTimer = ref(null);
const sessionTimeRemaining = ref(0);
const activityInterval = ref(null);

// Location-related state (Admin only)
const showLocationPermissionModal = ref(false);
const showLocationStatus = ref(false);
const locationPermissionRequested = ref(false);
const locationLoading = ref(false);
const locationError = ref(null);
const showLocationError = ref(false);

// Computed
const isAuthenticated = computed(() => authStore.isAuthenticated);

// Location computed properties (Admin only)
const locationEnabled = computed(
  () => authStore.locationStatus?.isEnabled || false
);

const locationStatusIcon = computed(() => {
  if (!authStore.locationStatus?.isEnabled) return NavigationOff;
  if (authStore.locationStatus?.isTracking) return Navigation;
  return Loader;
});

const locationStatusColor = computed(() => {
  if (!authStore.locationStatus?.isEnabled) return 'text-gray-400';
  if (authStore.locationStatus?.isTracking) return 'text-green-500';
  return 'text-yellow-500';
});

const locationStatusText = computed(() => {
  if (!authStore.locationStatus?.isEnabled) return 'Location Off';
  if (authStore.locationStatus?.isTracking) {
    const summary = authStore.currentLocationSummary;
    return summary?.display || 'Location Active';
  }
  return 'Connecting...';
});

const locationStatusClasses = computed(() => {
  if (!authStore.locationStatus?.isEnabled) {
    return 'border-gray-200';
  }
  if (authStore.locationStatus?.isTracking) {
    return 'border-green-200 bg-green-50';
  }
  return 'border-yellow-200 bg-yellow-50';
});

// Methods
const getTransitionName = () => 'fade';

const extendSession = () => {
  authStore.extendSession();
  showSessionWarning.value = false;
  clearTimeout(sessionWarningTimer.value);
  setupSessionWarning();
};

const handleSessionLogout = () => {
  showSessionWarning.value = false;
  authStore.logout();
  router.push('/login');
};

const setupSessionWarning = () => {
  if (!authStore.isAuthenticated) return;

  // Clear any existing timer
  if (sessionWarningTimer.value) {
    clearTimeout(sessionWarningTimer.value);
  }

  // Show warning 5 minutes before session expires
  const warningTime = Math.max(
    0,
    authStore.timeUntilSessionExpiry - 5 * 60 * 1000
  );

  if (warningTime > 0) {
    sessionWarningTimer.value = setTimeout(() => {
      if (authStore.isAuthenticated) {
        sessionTimeRemaining.value = authStore.timeUntilSessionExpiry;
        showSessionWarning.value = true;
      }
    }, warningTime);
  }
};

// Location-related methods (Admin only)
const checkLocationPermissionNeeded = () => {
  // Show location permission modal for ADMINS only (employees get forced tracking)
  if (
    authStore.isAuthenticated &&
    authStore.isAdmin && // Only for admins
    !locationPermissionRequested.value &&
    !authStore.locationStatus?.hasPermission &&
    !localStorage.getItem('admin_location_permission_dismissed')
  ) {
    setTimeout(() => {
      showLocationPermissionModal.value = true;
    }, 3000); // Show after 3 seconds to let user settle in
  }
};

const enableLocationServices = async () => {
  if (locationLoading.value) return;

  locationLoading.value = true;
  locationError.value = null;

  try {
    const enabled = await authStore.enableLocationTracking();

    if (enabled) {
      showLocationPermissionModal.value = false;
      showLocationStatus.value = true;
      locationPermissionRequested.value = true;

      // Hide status indicator after 8 seconds if successful
      setTimeout(() => {
        showLocationStatus.value = false;
      }, 8000);
    } else {
      throw new Error('Failed to enable location tracking');
    }
  } catch (error) {
    console.error('Failed to enable location services:', error);
    locationError.value = error.message || 'Failed to enable location services';
    showLocationError.value = true;

    // Auto-hide error after 10 seconds
    setTimeout(() => {
      showLocationError.value = false;
    }, 10000);
  } finally {
    locationLoading.value = false;
  }
};

const requestLocationPermission = async () => {
  if (locationLoading.value) return;

  locationLoading.value = true;
  try {
    await authStore.enableLocationTracking();
    showLocationStatus.value = true;
    setTimeout(() => {
      showLocationStatus.value = false;
    }, 5000);
  } catch (error) {
    console.error('Failed to request location permission:', error);
    locationError.value = error.message || 'Location permission denied';
    showLocationError.value = true;
    setTimeout(() => {
      showLocationError.value = false;
    }, 8000);
  } finally {
    locationLoading.value = false;
  }
};

const closeLocationPermissionModal = () => {
  showLocationPermissionModal.value = false;
  locationPermissionRequested.value = true;

  // Remember that admin dismissed the modal
  localStorage.setItem('admin_location_permission_dismissed', 'true');
};

const handleOnlineStatus = () => {
  isOffline.value = !navigator.onLine;

  if (navigator.onLine && authStore.isAuthenticated) {
    // Update activity when coming back online
    authStore.updateActivity();
  }
};

const handleVisibilityChange = () => {
  if (!document.hidden && authStore.isAuthenticated) {
    // Update activity when user returns to tab
    authStore.updateActivity();
    // Re-setup session warning in case time has passed
    setupSessionWarning();
  }
};

const handleBeforeUnload = () => {
  // Update last activity before page unload
  if (authStore.isAuthenticated) {
    authStore.updateActivity();
  }
};

const initializeApp = async () => {
  try {
    console.log('🚀 Initializing application...');

    // Initialize authentication
    await authStore.initializeAuth();

    // Start session monitoring if authenticated
    if (authStore.isAuthenticated) {
      authStore.startSessionMonitoring();
      setupSessionWarning();
      loadCurrency();
      appReady.value = true;
    } else if (route.meta.requiresAuth) {
      // Token was rejected by the server — redirect cleanly before any
      // protected components mount and trigger api.js 401 cascades.
      console.log('🚫 Auth init failed on protected route, redirecting to login');
      await router.push('/login');
      appReady.value = true;
    } else {
      // Public route (e.g., /login) — just show it
      appReady.value = true;
    }

    console.log('✅ Application initialized successfully');
  } catch (error) {
    console.error('❌ Failed to initialize application:', error);
    appReady.value = true; // Unblock UI even on error
  }
};

// Utility function for throttling
function throttle(func, limit) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Watch for authentication changes
watch(
  () => authStore.isAuthenticated,
  isAuth => {
    if (isAuth) {
      authStore.startSessionMonitoring();
      setupSessionWarning();
      loadCurrency();

      // Only check location permission for admins
      if (authStore.isAdmin) {
        checkLocationPermissionNeeded();

        // Show location status briefly after admin login
        setTimeout(() => {
          showLocationStatus.value = true;
          setTimeout(() => {
            showLocationStatus.value = false;
          }, 5000);
        }, 1000);
      }

      // For employees, location tracking is automatic and handled by EmployeeLocationNotification
    } else {
      authStore.stopSessionMonitoring();
      showSessionWarning.value = false;
      showLocationPermissionModal.value = false;
      showLocationStatus.value = false;
      showLocationError.value = false;

      if (sessionWarningTimer.value) {
        clearTimeout(sessionWarningTimer.value);
      }
    }
  }
);

// Watch for location status changes (Admin only)
watch(
  () => authStore.locationStatus?.isTracking,
  isTracking => {
    if (isTracking && authStore.isAuthenticated && authStore.isAdmin) {
      // Show brief confirmation when admin location tracking starts
      showLocationStatus.value = true;
      setTimeout(() => {
        showLocationStatus.value = false;
      }, 4000);
    }
  }
);

// Watch for location errors (Admin only)
watch(
  () => authStore.locationStatus?.error,
  error => {
    if (error && authStore.isAdmin) {
      locationError.value = error;
      showLocationError.value = true;
      setTimeout(() => {
        showLocationError.value = false;
      }, 8000);
    }
  }
);

// Lifecycle hooks
onMounted(async () => {
  try {
    // Initialize app
    await initializeApp();

    // Setup event listeners
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Initial online status check
    handleOnlineStatus();

    // Activity tracking for session management
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
    ];

    const throttledUpdateActivity = throttle(() => {
      if (authStore.isAuthenticated) {
        authStore.updateActivity();
      }
    }, 30000); // Update activity max once per 30 seconds

    activityEvents.forEach(event => {
      document.addEventListener(event, throttledUpdateActivity, true);
    });

    // Periodic session warning update
    activityInterval.value = setInterval(() => {
      if (showSessionWarning.value && authStore.isAuthenticated) {
        sessionTimeRemaining.value = authStore.timeUntilSessionExpiry;

        // Auto-logout if time expires
        if (sessionTimeRemaining.value <= 0) {
          handleSessionLogout();
        }
      }
    }, 1000);
  } catch (error) {
    console.error('❌ Failed to mount app:', error);
  }
});

onUnmounted(() => {
  // Cleanup event listeners
  window.removeEventListener('online', handleOnlineStatus);
  window.removeEventListener('offline', handleOnlineStatus);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('beforeunload', handleBeforeUnload);

  // Clear timers
  if (sessionWarningTimer.value) {
    clearTimeout(sessionWarningTimer.value);
  }

  if (activityInterval.value) {
    clearInterval(activityInterval.value);
  }

  // Stop session monitoring
  authStore.stopSessionMonitoring();
});
</script>

<style>
/* Global overflow prevention */
html, body {
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
}
</style>

<style scoped>
/* Transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.4s ease;
}

.slide-right-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.4s ease;
}

.slide-left-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Loading animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Modal animations */
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

/* Toast animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Responsive design */
@media (max-width: 768px) {
  .fixed.bottom-4.left-4.right-4 {
    left: 1rem;
    right: 1rem;
  }

  .fixed.bottom-4.right-4 {
    bottom: 1rem;
    right: 1rem;
    left: 1rem;
  }

  .fixed.top-4.right-4 {
    top: 1rem;
    right: 1rem;
    left: 1rem;
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active,
  .slide-left-enter-active,
  .slide-left-leave-active,
  .slide-right-enter-active,
  .slide-right-leave-active,
  .modal-enter-active,
  .modal-leave-active {
    transition: none;
  }
}

/* Focus management */
button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
</style>
